import React, { useState } from 'react';
import styled from 'styled-components';
import calendarUtils from '../utils/calendarUtils';

const CalendarSection = styled.div`
  background: #f0f8ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  
  @media (min-width: 768px) {
    padding: 20px;
    margin: 20px 0;
  }
`;

const CalendarTitle = styled.h4`
  margin: 0 0 12px 0;
  color: #0066cc;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (min-width: 768px) {
    font-size: 18px;
    margin: 0 0 15px 0;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  
  @media (min-width: 600px) {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
`;

const CalendarButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border: 2px solid #b3d9ff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #0066cc;
  transition: all 0.2s ease;
  min-height: 48px;
  
  &:hover {
    background: #e6f3ff;
    border-color: #007AFF;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (min-width: 768px) {
    padding: 14px 18px;
    font-size: 15px;
  }
`;

const CalendarIcon = styled.span`
  font-size: 18px;
  
  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const QuickAddButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #0056b3;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const EventDetails = styled.div`
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
  font-size: 13px;
  color: #666;
  border-left: 3px solid #007AFF;
`;

const CalendarIntegration = ({ booking, property, compact = false }) => {
  const [showAllOptions, setShowAllOptions] = useState(!compact);

  const handleQuickAdd = () => {
    const preferredCalendar = calendarUtils.detectPreferredCalendar();
    calendarUtils.openCalendar(preferredCalendar, booking, property);
  };

  const calendarOptions = calendarUtils.getAllCalendarOptions(booking, property);
  const preferredCalendar = calendarUtils.detectPreferredCalendar();
  const preferredOption = calendarOptions.find(option => option.type === preferredCalendar);

  const [year, month, day] = booking.showingDate.split('-');
  const [hours, minutes] = booking.showingTime.split(':');
  const showingDateTime = new Date(year, month - 1, day, hours, minutes);
  const formattedDateTime = showingDateTime.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });

  const address = `${property.address_line1}${property.unit_number ? ', ' + property.unit_number : ''}`;

  if (compact) {
    return (
      <CalendarSection>
        <CalendarTitle>
          📅 Add to Calendar
        </CalendarTitle>
        
        <QuickAddButton onClick={handleQuickAdd}>
          {preferredOption?.icon} Add to {preferredOption?.name || 'Calendar'}
        </QuickAddButton>
        
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => setShowAllOptions(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#007AFF',
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Other calendar options
          </button>
        </div>

        {showAllOptions && (
          <CalendarGrid style={{ marginTop: '12px' }}>
            {calendarOptions.map(option => (
              <CalendarButton
                key={option.type}
                onClick={option.action}
              >
                <CalendarIcon>{option.icon}</CalendarIcon>
                {option.name}
              </CalendarButton>
            ))}
          </CalendarGrid>
        )}
      </CalendarSection>
    );
  }

  return (
    <CalendarSection>
      <CalendarTitle>
        📅 Add to Your Calendar
      </CalendarTitle>
      
      <EventDetails>
        <strong>{formattedDateTime}</strong><br />
        📍 {address}<br />
        ⏱️ 30 minute showing<br />
        🔖 Confirmation: {booking.confirmation_number}
      </EventDetails>

      <CalendarGrid style={{ marginTop: '16px' }}>
        {calendarOptions.map(option => (
          <CalendarButton
            key={option.type}
            onClick={option.action}
          >
            <CalendarIcon>{option.icon}</CalendarIcon>
            {option.name}
          </CalendarButton>
        ))}
      </CalendarGrid>
      
      <div style={{ 
        fontSize: '12px', 
        color: '#666', 
        textAlign: 'center', 
        marginTop: '12px' 
      }}>
        📱 A reminder will be set for 15 minutes before your showing
      </div>
    </CalendarSection>
  );
};

export default CalendarIntegration;